import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SEO from "../components/SEO";
import { getPageSeo, breadcrumbSchema, caseStudySchema } from "../lib/seo";

const HollerbachCaseStudy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#101F27] text-white min-h-screen">
      <SEO
        {...getPageSeo("/case-studies/hollerbach-oktoberfest")}
        schema={[
          caseStudySchema("/case-studies/hollerbach-oktoberfest"),
          breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Case Studies", path: "/case-studies" },
          { name: "Hollerbach Oktoberfest", path: "/case-studies/hollerbach-oktoberfest" },
        ]),
        ]}
      />
      {/* Page Title Bar */}
      <section className="bg-[#37464F]/30 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-2">
              <Link
                to="/#case-studies"
                className="inline-flex items-center text-[#009688] hover:underline mb-6 text-sm font-semibold"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Case Studies
              </Link>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
                Hollerbach: Self-Serve Top Ups &amp; Reporting Solutions
              </h1>
            </div>
            <div className="lg:col-span-3">
              <div
                className="w-full h-72 md:h-96 rounded-lg bg-cover bg-center shadow-2xl"
                style={{
                  backgroundImage:
                    "url('/images/case-studies/hollerbach-card.png')",
                }}
                role="img"
                aria-label="Hollerbach Oktoberfest"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Case Study Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-invert max-w-none">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Overview</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-10">
              The Hollerbach Oktoberfest is a popular event held annually in the historic city of Sanford, Florida, attracting thousands of visitors. The event organizers faced numerous issues with the previous food and beverage ticket system, including long lineups and delays for attendees, inconsistent ticket sales, and challenges in tracking sales and revenue accurately. They were looking for an innovative solution for Oktoberfest 2022, so they chose WristCoin.
            </p>

            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Challenges</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-10">
              The previous food and beverage ticket system used at the event caused numerous issues for the organizers, including long lineups and delays for attendees, inconsistent ticket sales, and challenges in tracking sales and revenue accurately. The ticket system also didn't provide the reporting and analytics they needed to gain key insights into their customer's behavior. The solution needed to be able to handle the volume of the event, while also providing fast and reliable transactions and real-time reporting.
            </p>

            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Solution</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              Hollerbach chose WristCoin over other large competitors because of our innovative self-serve top-up feature and detailed real-time reporting. WristCoin's self-serve top-up feature allowed attendees to conveniently top up their wristbands with funds on their phone without an app, eliminating the need for food and beverage tickets while also reducing the number of necessary cashiers.
            </p>

            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              Watch Hollerbach's How-To Video for Attendees:
            </p>

            <div className="relative w-full mb-10 rounded-lg overflow-hidden shadow-2xl" style={{ paddingTop: "56.25%" }}>
              <iframe
                className="absolute inset-0 w-full h-full"
                src="https://www.youtube-nocookie.com/embed/1B_3IkE8ac4"
                title="Learn how to use your Hollerbach's Oktoberfest Festband"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              ></iframe>
            </div>

            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              In addition to the convenience for attendees, the self-serve top-up feature was also easy to set up and manage for the event organizers. Hollerbach was able to train all event staff to use the system without on-site support and eliminated the need to count tickets at the end of the event, saving both money and time.
            </p>

            <p className="text-lg text-gray-300 leading-relaxed mb-10">
              WristCoin's detailed reporting also solved a key concern of Hollerbach, allowing them to keep track of the success of different items across the event to properly allocate resources and plan for the coming years.
            </p>

            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Outcome</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              <a
                href="https://mywristcoin.com/convenient-self-serve-top-ups/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#009688] hover:underline"
              >
                The self-serve top-up feature
              </a>{" "}
              was a huge success, with attendees able to top up their wristbands from their phones without the need for cash or cashiers. The system eliminated lineups for cashiers, resulting in a more efficient and streamlined event. The convenience and ease of use of the self-serve top-up feature made it a hit with attendees, and Hollerbach was able to process revenue in the six figures using the system.
            </p>

            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              WristCoin also provided new insights into which items were the most popular, allowing organizers to optimize future events and provide better services to attendees. Following the event, we allowed Hollerbach to keep one active terminal free of charge to let their attendees spend the remainder of their balance at Hollerbach's Orlando restaurant. Overall, Hollerbach was very happy with their closed-loop cashless event solution and plans to use the system again for Oktoberfest 2023.
            </p>
          </article>
        </div>
      </section>
    </div>
  );
};

export default HollerbachCaseStudy;
